import React from 'react';

import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import type { SearchResultCommitPartial } from '@atlassian/dynamic-cards-search-client/graphql/third-party-graph-entity';

import { Avatar } from '../../../../../../../common/ui/avatar';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	enrichExcerpt,
	getIntegrationKey,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useBootstrap, usePrimaryProduct } from '../../../../../../../controllers/store';
import { messages } from './messages';

interface CommitSearchResultProps {
	icon: React.ReactNode;
	partial: SearchResultCommitPartial;
	position?: number;
}

export const CommitSearchResult = ({
	partial,
	position,
	icon,
}: CommitSearchResultProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId }] = useBootstrap();

	const { lastModifiedDate, url, permissionLevel, title, excerpt, entity } = partial;
	const { author, container } = entity || {};
	const label = formatMessage(messages.resultLabel);
	const integrationKey = getIntegrationKey(partial.integrationId);

	const lastModifiedComponent = lastModifiedDate ? (
		<LastModified lastModified={lastModifiedDate} />
	) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	let messageDescription;
	if (author?.user) {
		messageDescription = (
			<Inline space="space.025" alignBlock="start">
				<Avatar name={author.user.name} />
				<Box>{processExcerpt(excerpt, true, `${author.user.name}: `)}</Box>
			</Inline>
		);
	} else {
		messageDescription = <Box>{processExcerpt(excerpt, true)}</Box>;
	}

	return (
		<SearchResult
			id={partial.id}
			icon={icon}
			description={messageDescription}
			headerMetadata={
				<SearchMetadata
					product={integrationKey}
					productSelected={true}
					parts={[
						label,
						['repository-name', container?.name ?? null],
						['padlock', padlockIcon],
						['last-modified', lastModifiedComponent],
					]}
				/>
			}
			title={enrichExcerpt(title)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={partial}
		/>
	);
};
